import React, {
  memo, useState, useContext, useRef, useEffect,
} from 'react';
import {
  Button, Input, Layout, Menu, notification,
} from 'antd';
import { useMutation } from 'react-query';
import { useLocation, useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import MediaQuery from 'react-responsive';
import classnames from 'classnames';
import { DownOutlined, SearchOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useAuthenticator } from '@aws-amplify/ui-react';
import './styles.scss';
import ProfileMenu from './ProfileMenu';
import MobileMenu from './MobileMenu';
import AboutGlosModal from './AboutGlosModal';
import SeagullCoastModal from './SeagullCoastModal';
import Logo from './Logo';
import { CloseIcon, MenuIcon } from '../icons';
import SearchDrawer from '../search-drawer';
import { featureFlag } from '../../constant';
import UserContext from '../../contexts/UserContext';
import PreferencesMenu from './PreferencesMenu';
import { createUserFavorite } from '../../services/profile.service';

const { Header } = Layout;

const TopBar = (props) => {
  const {
    logoVisible,
    getSelectedKey,
    setAuthDrawerOpen,
  } = props;

  const [visible, setVisible] = useState(false);
  const [searchVisible, setSearchVisible] = useState(false);
  const [aboutGlosOpen, setAboutGlosOpen] = useState(false);
  const [seagullCoastOpen, setSeagullCoastOpen] = useState(false);

  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);

  const addFavoriteMutation = useMutation((id) => createUserFavorite(id));

  useEffect(() => {
    if (authStatus === 'authenticated') {
      const pendingId = sessionStorage.getItem('pendingFavoriteId');
      if (pendingId) {
        sessionStorage.removeItem('pendingFavoriteId');
        addFavoriteMutation.mutate(
          { obs_dataset_id: parseInt(pendingId, 10), nickname: '' },
          {
            onSuccess: () => {
              notification.success({
                message: 'Platform favorited',
                description: 'The platform has been added to your favorites.',
              });
            },
          },
        );
      }
    }
  }, [authStatus]);

  const {
    globalSearch,
    setGlobalSearch,
    setSearchActive,
  } = useContext(UserContext);

  const location = useLocation();
  const searchBtn = useRef(null);

  const onClick = () => setVisible(!visible);

  const onClose = () => setVisible(false);

  const onCloseSearch = () => setSearchVisible(false);

  const history = useHistory();

  const showSearch = () => {
    if (location.pathname.includes('/landing')) {
      history.replace('/map');
    }
    setSearchVisible(!searchVisible);
  };

  const onChange = (e) => setGlobalSearch(e.target.value);

  const onEnter = (e) => {
    if (e.key === 'Enter') {
      searchBtn?.current.click();
    }
  };

  const onClickSignIn = () => {
    setAuthDrawerOpen(true);
  };

  const selectedKey = getSelectedKey();

  useEffect(() => {
    if (location.pathname !== '/search') {
      setGlobalSearch('');
    }
  }, [location]);

  return (
    <>
      <Header className={classnames('top-bar')}>
        <div className="mobile-menu">
          {visible ? <CloseIcon onClick={onClose} style={{ fontSize: 28, color: '#fff', width: 20 }} />
            : <Button style={{ width: 40 }} icon={<MenuIcon />} onClick={onClick} />}
        </div>
        <Logo visible={logoVisible} setAuthDrawerOpen={setAuthDrawerOpen} />
        <div className="top-bar-left">
          <Menu theme="dark" mode="horizontal" selectedKeys={[selectedKey]}>
            <Menu.Item key="map">
              <a
                onClick={() => {
                  setAuthDrawerOpen(false);
                  history.push(sessionStorage.getItem('lastMapUrl') || '/map');
                }}
              >
                <span>Map</span>
              </a>
            </Menu.Item>
            <Menu.Item key="data-console">
              <a
                onClick={() => {
                  setAuthDrawerOpen(false);
                  history.push(sessionStorage.getItem('lastConsoleUrl') || '/data-console');
                }}
              >
                <span>Console</span>
              </a>
            </Menu.Item>
            <Menu.SubMenu
              key="about"
              popupClassName="top-bar-dropdown"
              popupOffset={[-15, -6]}
              title={(
                <span className="menu-dropdown-title">
                  <span>About</span>
                  <DownOutlined />
                </span>
              )}
            >
              <Menu.Item key="about-glos">
                <a onClick={() => setAboutGlosOpen(true)}>About GLOS</a>
              </Menu.Item>
              <Menu.Item key="seagull-coast">
                <a onClick={() => setSeagullCoastOpen(true)}>Seagull Coast</a>
              </Menu.Item>
              <Menu.Item key="support-seagull">
                <Link to="/support-seagull" onClick={() => setAuthDrawerOpen(false)}>Support GLOS</Link>
              </Menu.Item>
              <Menu.Item key="privacy-policy">
                <a href="https://glos.org/legal/">Legal</a>
              </Menu.Item>
              <Menu.Item key="legal">
                <a href="https://glos.org/privacy-policy/">Privacy Policy</a>
              </Menu.Item>
            </Menu.SubMenu>
          </Menu>
        </div>

        <div className="top-bar-right">
          { !visible
            ? (
              <>
                <MediaQuery minWidth={767}>
                  <Input
                    className="searchbox"
                    placeholder="Search Seagull by Keyword"
                    onChange={onChange}
                    onKeyPress={onEnter}
                    size="small"
                    value={globalSearch}
                    suffix={(
                      <Link to={`/search?query=${globalSearch}`} ref={searchBtn} onClick={() => setSearchActive(true)}>
                        <Button
                          type="primary"
                          shape="circle"
                          icon={<SearchOutlined />}
                        />
                      </Link>
                    )}
                  />
                </MediaQuery>
                <MediaQuery maxWidth={767}>
                  {searchVisible ? <CloseIcon onClick={onCloseSearch} style={{ fontSize: 28, color: '#fff' }} />
                    : <SearchOutlined style={{ fontSize: 20, color: '#fff' }} onClick={showSearch} /> }
                </MediaQuery>
              </>
            ) : '' }
          {featureFlag.userPreferences && authStatus !== 'authenticated' && (
            <MediaQuery minWidth={767}>
              <PreferencesMenu />
            </MediaQuery>
          )}
          {featureFlag.userProfile && (
            <MediaQuery minWidth={767}>
              <ProfileMenu isMobile={false} onClickSignIn={onClickSignIn} />
            </MediaQuery>
          )}
        </div>
      </Header>
      <MobileMenu
        visible={visible}
        onClose={onClose}
        onClickSignIn={onClickSignIn}
        onAboutGlosClick={() => setAboutGlosOpen(true)}
        onSeagullCoastClick={() => setSeagullCoastOpen(true)}
      />
      <AboutGlosModal open={aboutGlosOpen} onClose={() => setAboutGlosOpen(false)} />
      <SeagullCoastModal open={seagullCoastOpen} onClose={() => setSeagullCoastOpen(false)} />
      {!location.pathname?.includes('/landing')
        ? <SearchDrawer visible={searchVisible} onClose={onCloseSearch} setSearchVisible={setSearchVisible} /> : ''}
    </>
  );
};

TopBar.propTypes = {
  logoVisible: PropTypes.bool,
  getSelectedKey: PropTypes.func,
  setAuthDrawerOpen: PropTypes.func,
  // handleSignOut: PropTypes.func,
};

TopBar.defaultProps = {
  logoVisible: true,
  getSelectedKey: () => {},
  setAuthDrawerOpen: () => {},
  // handleSignOut: () => {},
};

export default memo(TopBar);
